/**
 * Creates dynamic triggering of questions using json string on CF7 fields
 *  DATA Object structure for data-js-trigger array of {value-that-triggers-field: trigger-this-field-id}
 * @returns {[undefined]} []
 */

export function dynamicFieldsInit() {
  const form = $('[data-js-form-dynamic-cf7]').parent('form');
  const visibilityField = 'is-visible';

  function findKeys(object, value) {
    const foundKeys = [];

    for (const key in object) {
      if (Object.values(object[key])[0] === value) {
        foundKeys.push(Object.keys(object[key])[0]);
      }
    }
    return foundKeys;
  }

  function clearOnChange($element) {
    $element
      .find('select, input:not([type="checkbox"])')
      .each((index, element) => {
        $(element).val('');
      });
  }

  function getFieldType($question) {
    if ($question.find('input[type=radio]').length > 0) {
      return 'radio';
    } else if ($question.find('select').length > 0) {
      return 'select';
    } else if ($question.find('input[type=checkbox]').length > 0) {
      return 'checkbox';
    }
  }

  /**
   * Toggles the visibility of a target field based on the type and value of a trigger field.
   *
   * @param {string} type - The type of the trigger field ('radio', 'select', 'checkbox').
   * @param {Object} target - The target field whose visibility is to be toggled.
   * @param {Object} triggerField - The field that triggers the visibility change.
   * @param {string} triggerVal - The value of the trigger field that triggers the visibility change (used for checkboxes).
   * @param {Array} otherValues - An array of values that trigger the visibility change (used for radio and select fields).
   */
  function maybeShowField(type, target, triggerField, triggerVal, otherValues) {
    switch (type) {
      case 'radio':
        target.toggleClass(
          visibilityField,
          triggerField.checked && otherValues.includes(triggerField.value)
        );
        break;
      case 'select':
        target.toggleClass(
          visibilityField,
          otherValues.includes(triggerField.value)
        );
        if (!target.hasClass(visibilityField)) {
          clearOnChange(target);
        }
        break;
      case 'checkbox':
        if (triggerField.value === triggerVal) {
          target.toggleClass(visibilityField, triggerField.checked);
        }
        break;
    }
  }

  /**
   * Retrieves the value of a form field by its name.
   *
   * Helpful since different input types reference values differently.
   *
   * @param {string} name - The name attribute of the form field to retrieve the value from.
   * @returns {string} The value of the form field, or an empty string if the field is not found.
   */
  function getFieldValue(name){
    const selectField = form.find(`select[name=${name}]`);

    if (selectField.length > 0) {
      return selectField.val();
    }

    return form.find(`input[name=${name}]:checked`).val() ?? "";
  }

  function shouldElementShow(fieldMap) {
    const conditionsMet = fieldMap.map(
      (condition) => {
      const shouldShow = Object.keys(condition).map(
        key => {
          const fieldVal = getFieldValue(key);
          return condition[key].includes(fieldVal);
        }
      );
      return shouldShow.every(v => v);
    });

    return conditionsMet.some(condition => condition);
  }

  /**
   * Sets up dynamic fields based on the provided question element.
   *
   * @param {HTMLElement} question - The question element containing data attributes for dynamic field setup.
   * @returns {void}
   *
   * @throws {SyntaxError} Will throw an error if the JSON parsing of the data attribute fails.
   */
  function setupDynamicField(question) {
    const $question = $(question);
    let jsonData;

    try {
      jsonData = JSON.parse($question.attr('data-js-trigger'));
    } catch (e) {
      console.error(e);
      return;
    }

    const fieldType = getFieldType($question);
    const $inputs = $question.find('input[type=radio], select, input[type=checkbox]');

    for (const data of jsonData) {
      const val = Object.keys(data)[0];
      const idToTrigger = data[val];
      // All values that could also show the question
      const otherValuesToTrigger = findKeys(jsonData, idToTrigger);

      if (!idToTrigger) continue;

      const element = $(`#${idToTrigger}`);

      $inputs.on('change', (e) => {
        maybeShowField(fieldType, element, e.currentTarget, val, otherValuesToTrigger);
      });
    }
  }

  /**
   * Sets up a conditional field based on the provided configuration.
   *
   * @param {HTMLElement} conditional - The DOM element that has the conditional field configuration.
   * @throws {SyntaxError} Will throw an error if the JSON parsing of the data attribute fails.
   */
  function setupConditionalField(conditional) {
    const $conditional = $(conditional);
    let jsonData;

    try {
      jsonData = JSON.parse($conditional.attr('data-js-conditional'));
    } catch (e) {
      console.error(e);
      return;
    }

    form.on('change', () => {
      $conditional.toggleClass(
        visibilityField,
        shouldElementShow(jsonData)
      )
    });

    $conditional.toggleClass(
      visibilityField,
      shouldElementShow(jsonData)
    );
  }

  if (form.length > 0) {
    for (const formElement of form) {
      const triggerQuestions = $(formElement).find('[data-js-trigger]');
      const conditionalQuestions = $(formElement).find('[data-js-conditional]');

      triggerQuestions.each((i, el) => setupDynamicField(el));
      conditionalQuestions.each((i, el) => setupConditionalField(el));
    }
  }
}
